import React, { useState, useEffect, useContext } from 'react'
import { css } from '@emotion/core'
import { useTranslation } from 'react-i18next'
import { useAppDispatch } from '@/features/hooks'
import { editingSectionActions } from '@/features/reservation-detail/editing-section-slice'
import 'dayjs/locale/ja'
import dayjs from 'dayjs'

import {
  putAdminReservation,
  fetchReservationChannelCode,
  deleteSalesAdvancePayment,
  validateReservationDate,
  fetchReservationMarketSegment,
} from '@/apis/aipass'
import { ReservationStatusType } from '@/models/reservation-reservation-status'
import { ApprovedStatusType } from '@/models/reservation-approved-status'
import { useErrorHandler } from '@/hooks/use-error-handler'
import { Button } from '@/components/atoms/button'
import { ReservationUserGuest } from '@/components/organisms/customer/_reservation-user-guest/reservation-usr-guest'
import { DatePicker } from '@/components/organisms/reservation/_detail-edit-info/date-picker'
import { LoadingFull } from '@/components/molecules/loading-full'
import { ShortText } from '@/components/atoms/short-text'
import { checkActivePlugin, PluginCode } from '@/libs/plugins'
import { AccountContext } from '@/contexts/account'
import { useAssignConfirmDialog } from '../../reservation/use-assign-confirm-dialog'
import { initializeSelectBoxStyle } from '@/constants/form'
import { Hint } from '@/components/atoms/hint'
import { TimePicker } from '@/components/atoms/time-picker'
import moment from 'moment'
import { Popup } from '@/components/atoms/popup'
import { ReservationType } from '@/models/reservation'
import { ChannelCodeType } from '@/models/reservation-channel-code'
import { MarketSegmentType } from '@/models/reservation-market-segment'
import { env } from '@/libs/env'

type PlanInfoProps = {
  reservation: ReservationType
  onChangeReservation?: () => void
  isEdit: boolean
  setIsEdit: (v: boolean) => void
  onChangeReservationStatus: () => void
}

export const PlanInfoDetail: React.FC<PlanInfoProps> = ({
  reservation,
  onChangeReservation,
  isEdit,
  setIsEdit,
  onChangeReservationStatus,
}) => {
  const aipassZendeskUrl = env('AIPASS_ZENDESK_URL')
  const { t, i18n } = useTranslation()
  const { plugins } = useContext<any>(AccountContext)
  const { errorHandler, isOutOfStockError } = useErrorHandler()

  const dispatch = useAppDispatch()
  const [reservationDetailOpen, setReservationDetailOpen] = useState<boolean>(false)
  const [isDropDownOpen, setIsDropDownOpen] = useState<boolean>(false)
  const [isEditLoading, setIsEditLoading] = useState<boolean>(false)
  const [showEditFooter, setShowEditFooter] = useState<boolean>(false)
  const [isTelegramDataVisible, setIsTelegramDataVisible] = useState<boolean>(false)
  const [channelCodeId, setChannelCodeId] = useState(reservation?.channelCodeId)
  const [marketSegmentId, setMarketSegmentId] = useState(reservation?.marketSegmentId)
  const [channelCodeMasters, setChannelCodeMasters] = useState<ChannelCodeType[]>([])
  const [segmentMasters, setSegmentMasters] = useState<MarketSegmentType[]>([])
  const [checkinDate, setCheckinDate] = useState(reservation?.checkinDate)
  const [checkoutDate, setCheckoutDate] = useState(reservation?.checkoutDate)
  const [checkinTime, setCheckinTime] = useState(reservation?.checkinTime)
  const [checkoutTime, setCheckoutTime] = useState(reservation?.checkoutTime)
  const { AssignConfirmDialog, openConfirmDialog } = useAssignConfirmDialog()

  const handleReservationDateValidation = async (checkinDate: string, checkoutDate: string) => {
    const response = await validateReservationDate({
      reservationId: reservation.reservationId,
      checkinId: reservation.checkinId,
      checkinDate,
      checkoutDate,
    })
    return response.valid
  }

  const isChangedCheckinDate = () => dayjs(checkinDate).format('YYYY-MM-DD') !== dayjs(reservation?.checkinDate).format('YYYY-MM-DD')
  const isChangedCheckoutDate = () => dayjs(checkoutDate).format('YYYY-MM-DD') !== dayjs(reservation?.checkoutDate).format('YYYY-MM-DD')

  // Reservation status change
  const updateReservationStatus = async reservationStatus => {
    try {
      const isCancelOrNoshow =
        reservationStatus === ReservationStatusType.CancellationReport || reservationStatus === ReservationStatusType.NoshowReport

      if (isCancelOrNoshow) {
        // チェックイン済みの場合、部屋紐付けが解除されることを警告する
        if (
          reservation.approvedStatus === ApprovedStatusType.Checkin &&
          !window.confirm(t('Are you sure you want to unlink the reservation'))
        )
          return

        /**
         * tllincoln_status：0=手動/CSV、1=TL、3=TL以外のサイトコントローラー
         * ネット予約かつ2wayプラグインが有効な場合のみ在庫調整の確認が必要
         */
        const isNetReservation = [1, 3].includes(reservation.tllincolnStatus)
        const hasTwoWayPlugin =
          checkActivePlugin(plugins, PluginCode.ReservationTLLincolnTwoWay) ||
          checkActivePlugin(plugins, PluginCode.ReservationNeppanTwoWay) ||
          checkActivePlugin(plugins, PluginCode.ReservationTemairazuTwoWay) ||
          checkActivePlugin(plugins, PluginCode.ReservationTriplaTwoWay)

        if (
          isNetReservation &&
          hasTwoWayPlugin &&
          !window.confirm(
            t('Its an online reservation If you change or delete the reservation details you may need to adjust the inventory separately'),
          )
        )
          return
      }

      setIsEditLoading(true)
      await _updateReservation({ optionStatus: reservationStatus })
      onChangeReservationStatus()
    } catch (error) {
      errorHandler(error)
    } finally {
      setIsEditLoading(false)
    }
  }

  const _displayMeal = meal => {
    // Corresponds to accommodation and meal conditions that are already in the production DB
    if (meal === '朝食付き' || meal === '朝食付' || meal === '朝食が客室料金に含まれています。') {
      return '朝食'
    }
    if (meal === '夕食付き' || meal === '夕食付') {
      return '夕食'
    }
    if (meal === '朝夕食付き' || meal === '朝夕食付' || meal === '夕朝食付') {
      return '朝食/夕食'
    }
    if (meal === '2食付' || meal === '1泊2食') {
      return '2食付き'
    }
    if (meal === '朝昼夕食付き') {
      return '朝食/昼食/夕食'
    }
    // Arrange the applicable lodging and meal conditions in an array without specifying
    const unspecifiedList = [
      undefined,
      '',
      null,
      '指定なし',
      '食事別',
      '食事なし',
      '素泊まり',
      'この客室にはお食事のオプションはありません。',
      '素泊り',
      '朝食が客室料金に含まれています。',
      '他',
      '露天風呂の間2名様　B期間',
      'その他',
      'ツアー代',
      '泊食条件',
      'ツイン２名様　S期間',
      '露天風呂の間2名様　A期間',
    ]
    if (unspecifiedList.includes(meal)) {
      return '-'
    }

    // From now on, accommodation and food conditions will be saved in the DB by either of the following
    // Breakfast / Lunch / Dinner / 1 board / 2 meals / -
    return meal
  }

  const _displayChild = childs => {
    if (
      !childs?.paxChildA &&
      !childs?.paxChildB &&
      !childs?.paxChildC &&
      !childs?.paxChildD &&
      !childs?.paxChildE &&
      !childs?.paxChildF &&
      !childs?.paxChildOther
    ) {
      return ''
    }
    return (
      <div css={displayChildStyle}>
        {t('Child')} : A {childs?.paxChildA}
        {t('Name')} B {childs?.paxChildB}
        {t('Name')} C {childs?.paxChildC}
        {t('Name')} D {childs?.paxChildD}
        {t('Name')} E {childs?.paxChildE}
        {t('Name')} F {childs?.paxChildF}
        {t('Name')} {t('Others')} {childs?.paxChildOther}
        {t('Name')}
        <a target="_blank" rel="noopener noreferrer" href={`${aipassZendeskUrl}/hc/ja/articles/16229000356249-子どもの表記区分について`}>
          <img alt="help" src={require('@/static/images/help.svg')} style={{ paddingLeft: 11 }} />
        </a>
      </div>
    )
  }

  const _reservationGuestRoom = () => {
    if (reservation?.reservationRoom?.length === 0) {
      return <li>- / -</li>
    }
    return reservation.reservationRoom.map((v, index) => {
      const roomName = i18n.language === 'ja' ? v.typeNameJa : v.typeNameEn
      return <li key={index}>{`${roomName ? roomName : '-'} / ${v.roomNum}${t('room counter')}`}</li>
    })
  }

  const getReservationDate = (reservation: any): string => {
    if (!reservation || !reservation.reservationDate) {
      return '-'
    }
    return dayjs(reservation.reservationDate).format(t('YYYY-MM-DD HH:mm'))
  }

  const getReservationUpdateDate = (reservation: any): string => {
    if (!reservation || !reservation.statusUpdatedAt) {
      return '-'
    }
    return dayjs(reservation.statusUpdatedAt).format(t('YYYY-MM-DD HH:mm'))
  }

  const updateChannelCode = (selectedId: string) => {
    setChannelCodeId(selectedId)
    const selectedChannel = channelCodeMasters.find(master => master.id === selectedId)
    if (!selectedChannel) {
      return
    }
    if (selectedChannel.suggestSegmentId && !marketSegmentId) {
      const isEnabledSegment = segmentMasters.findIndex(master => master.id === selectedChannel.suggestSegmentId) !== -1
      if (isEnabledSegment) {
        setMarketSegmentId(selectedChannel.suggestSegmentId)
      }
    }
  }

  const _validate = (): boolean => {
    // Accommodation date before change
    const checkinDateWithoutHour = checkinDate?.includes(' ') ? checkinDate.split(' ')[0] : checkinDate // Default BE return YYYY-MM-DD HH:MM:SS
    const checkoutDateWithoutHour = checkoutDate?.includes(' ') ? checkoutDate.split(' ')[0] : checkoutDate // Default BE return YYYY-MM-DD HH:MM:SS

    if (dayjs(checkoutDate).format('YYYY-MM-DD') < dayjs(checkinDate).format('YYYY-MM-DD')) {
      window.alert(t('Please set the check-out date after the check-in date'))
      return false
    }

    if (
      checkinTime &&
      checkoutTime &&
      dayjs(`${checkoutDateWithoutHour} ${checkoutTime}`).isSameOrBefore(`${checkinDateWithoutHour} ${checkinTime}`)
    ) {
      window.alert(t('Checkout time before checkin time'))
      return false
    }

    return true
  }

  const _updateReservation = async (
    updateValues:
      | {
          checkinDate: string
          checkoutDate: string
          checkinTime: string
          checkoutTime: string
          channelCodeId: string | undefined
          marketSegmentId: string | undefined
        }
      | { optionStatus: ReservationStatusType },

    option?: { validationOnly?: boolean; excludeStockCheck?: boolean },
  ) => {
    const data = {
      reservationId: reservation.reservationId,
      reservation: {
        checkinDate: dayjs(reservation.checkinDate).format('YYYY-MM-DD'),
        checkoutDate: dayjs(reservation.checkoutDate).format('YYYY-MM-DD'),
        checkinTime: reservation.checkinTime,
        checkoutTime: reservation.checkoutTime,
        meal: reservation.meal,
        method: reservation.method,
        nights: reservation.nights,
        notes: reservation.notes,
        payment: reservation.payment,
        plan: reservation.plan,
        accommodationFee: reservation.accommodationFee,
        pointDiscount: reservation.pointDiscount,
        optionStatus: reservation.reservationStatus,
        channelCodeId: reservation.channelCodeId,
        marketSegmentId: reservation.marketSegmentId,
        ...updateValues,
      },
    }
    await putAdminReservation({ ...data, ...option })
  }

  const onSaveData = async () => {
    try {
      setIsEditLoading(true)
      if (!_validate()) return

      const isValid = await handleReservationDateValidation(checkinDate, checkoutDate)

      if (!isValid) {
        window.alert(t('The sales date is outside the stay period. Please manually change the sales date.'))
      }
      const updateValues = { checkinDate, checkoutDate, checkinTime, checkoutTime, channelCodeId, marketSegmentId }
      try {
        await _updateReservation(updateValues, { validationOnly: true })
      } catch (e) {
        if (!isOutOfStockError(e as Error)) {
          throw e
        }
        setIsEditLoading(false)
        if (!(await openConfirmDialog())) {
          return
        }
        setIsEditLoading(true)
      }

      if (isChangedCheckinDate()) {
        // Remove down payment flag
        await deleteSalesAdvancePayment(reservation.reservationId).catch(() => {
          console.log(t('Communication failed'))
        })
      }

      await _updateReservation(updateValues, { excludeStockCheck: true })
      if (onChangeReservation) onChangeReservation()

      setShowEditFooter(false)
    } catch (error) {
      errorHandler(error)
    } finally {
      setIsEditLoading(false)
    }
  }

  const resetReservationData = () => {
    setCheckinDate(reservation?.checkinDate)
    setCheckoutDate(reservation?.checkoutDate)
    setCheckinTime(reservation?.checkinTime)
    setCheckoutTime(reservation?.checkoutTime)
    setChannelCodeId(reservation?.channelCodeId)
    setMarketSegmentId(reservation?.marketSegmentId)
  }

  useEffect(() => {
    fetchReservationChannelCode().then(setChannelCodeMasters)
    fetchReservationMarketSegment().then(setSegmentMasters)
  }, [])

  useEffect(() => {
    resetReservationData()
    setIsTelegramDataVisible(false)
  }, [reservation])

  useEffect(() => {
    if (
      isChangedCheckinDate() ||
      isChangedCheckoutDate() ||
      checkinTime !== reservation?.checkinTime ||
      checkoutTime !== reservation?.checkoutTime ||
      channelCodeId !== reservation?.channelCodeId ||
      marketSegmentId !== reservation?.marketSegmentId
    ) {
      setShowEditFooter(true)
    } else {
      setShowEditFooter(false)
    }
  }, [checkinDate, checkoutDate, checkinTime, checkoutTime, channelCodeId, marketSegmentId])

  useEffect(() => {
    dispatch(editingSectionActions.setEditingSection({ key: 'reservationInfo', value: showEditFooter }))
    return () => {
      dispatch(editingSectionActions.setEditingSection({ key: 'reservationInfo', value: false }))
    }
  }, [showEditFooter])

  return (
    <div css={{ position: 'relative', overflowWrap: 'anywhere' }}>
      <LoadingFull isLoading={isEditLoading} />
      <div css={reservationInfoStyle}>
        <div css={topContentStyle}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 24 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <p style={{ fontSize: 14, fontWeight: 'bold' }}>{t('Reservation Info')}</p>
              <Hint customCss={hintStyle}>
                <p style={{ fontSize: 12, fontWeight: 'bold', color: '#676767', padding: 16, lineHeight: 1.75 }}>
                  {t(
                    'For manual reservations, you only need to enter the check-in and check-out dates, channel, and (plan name). For reservation linkage, the information at the time of reservation will be automatically entered.',
                  )}
                </p>
              </Hint>
            </div>
            {reservation && (
              <div css={reservationStatusStyle} onClick={() => setIsDropDownOpen(!isDropDownOpen)}>
                {reservation.reservationStatus === ReservationStatusType.NewReport && <>{t('New reservation')}</>}
                {reservation.reservationStatus === ReservationStatusType.ModificationReport && <>{t('Update reservation')}</>}
                {reservation.reservationStatus === ReservationStatusType.CancellationReport && <>{t('Cancel')}</>}
                {reservation.reservationStatus === ReservationStatusType.NoshowReport && <>{t('No show')}</>}
                <img css={arrowStyle} src={require('@/static/images/arrow_yellow.svg')} alt="" />
                <Popup isOpen={isDropDownOpen} onClose={() => setIsDropDownOpen(false)} customStyle={css({ top: 30 })}>
                  <ul>
                    {reservation.reservationStatus !== ReservationStatusType.NewReport && (
                      <li
                        css={dropDownContentStyle}
                        onClick={() => {
                          updateReservationStatus(ReservationStatusType.NewReport)
                        }}
                      >
                        <p css={dropDownTitleStyle}>{t('New reservation')}</p>
                      </li>
                    )}
                    {reservation.reservationStatus !== ReservationStatusType.ModificationReport && (
                      <li
                        css={dropDownContentStyle}
                        onClick={() => {
                          updateReservationStatus(ReservationStatusType.ModificationReport)
                        }}
                      >
                        <p css={dropDownTitleStyle}>{t('Update reservation')}</p>
                      </li>
                    )}
                    {reservation.reservationStatus !== ReservationStatusType.CancellationReport && (
                      <li
                        css={dropDownContentStyle}
                        onClick={() => {
                          updateReservationStatus(ReservationStatusType.CancellationReport)
                        }}
                      >
                        <p css={dropDownTitleStyle}>{t('Cancel')}</p>
                      </li>
                    )}
                    {reservation.reservationStatus !== ReservationStatusType.NoshowReport && (
                      <li
                        css={dropDownContentStyle}
                        onClick={() => {
                          updateReservationStatus(ReservationStatusType.NoshowReport)
                        }}
                      >
                        <p css={dropDownTitleStyle}>{t('No show')}</p>
                      </li>
                    )}
                  </ul>
                </Popup>
              </div>
            )}
          </div>
          <div css={{ display: 'flex', alignItems: 'center' }}>
            <table style={{ color: '#676767', fontSize: 12 }}>
              <tr>
                <td style={{ textAlign: 'right' }}>{t('Reserve')}：</td>
                <td>{getReservationDate(reservation)}</td>
              </tr>
              <tr>
                <td style={{ textAlign: 'right', paddingTop: 8 }}>{t('Update/Cancel')}：</td>
                <td>{getReservationUpdateDate(reservation)}</td>
              </tr>
            </table>
            <div css={[planEditButtonStyle, { marginLeft: 20 }]} onClick={() => setIsEdit(!isEdit)}>
              <img alt="edit" src={require('@/static/images/edit.svg')} />
            </div>
          </div>
        </div>
        {/* Full booking details */}
        <div css={listWrapperStyle}>
          <div>
            <div style={{ padding: 32 }}>
              {/* Date of stay */}
              <div>
                <div css={checkInDateStyle}>
                  <img alt="icon_plan_name" src={require('@/static/images/date.svg')} />
                  <div>
                    <div
                      css={{
                        display: 'grid',
                        alignItems: 'center',
                        gridTemplateColumns: '1fr auto 1fr',
                        gridTemplateRows: 'repeat(2, 1fr)',
                        gridGap: '16px 0',
                      }}
                    >
                      <DatePicker date={checkinDate} onDateChange={setCheckinDate} />
                      <span css={wavyLineStyle}>~</span>
                      <DatePicker date={checkoutDate} onDateChange={setCheckoutDate} />

                      <TimePicker
                        minuteStep={15}
                        value={checkinTime ? (moment(checkinTime, 'HH:mm') as any) : undefined}
                        onChange={value => {
                          setCheckinTime(value?.format('HH:mm'))
                        }}
                      />
                      <span />
                      <TimePicker
                        minuteStep={15}
                        value={checkoutTime ? (moment(checkoutTime, 'HH:mm') as any) : undefined}
                        onChange={value => {
                          setCheckoutTime(value?.format('HH:mm'))
                        }}
                      />
                    </div>
                  </div>
                </div>
              </div>
              {/* Number of guests */}
              <div css={numberOfCustomerStyle}>
                <img alt="people" src={require('@/static/images/people.svg')} />
                <ul>{reservation?.paxTotal ? reservation.paxTotal + t('Name') : '-'}</ul>
              </div>
              <div css={numberOfCustomerDetailStyle}>
                {reservation ? (
                  <>
                    {t('Adult')} : {t('Male')}
                    <span>{reservation.paxMale !== 0 ? reservation.paxMale + t('Name') : '-'}</span>
                    {t('Female')}
                    <span>{reservation.paxFemale !== 0 ? reservation.paxFemale + t('Name') : '-'}</span>
                  </>
                ) : (
                  <li>-</li>
                )}
                {_displayChild(reservation)}
              </div>

              {/* memo */}
              <div css={listMemoStyle}>
                <img alt="memo" src={require('@/static/images/memo.svg')} />
                <ShortText>{reservation?.notes || '-'}</ShortText>
              </div>
            </div>
          </div>
          <div className="divider" />
          <div>
            <div style={{ padding: '32px 32px 0px', paddingBottom: reservation.telegramData ? 0 : '32px' }}>
              {/* channel code name */}
              <div css={[reservationRoomContainerStyle, { marginTop: 0 }]}>
                <img alt="icon_plan_name" src={require('@/static/images/chanel.svg')} />
                <div>
                  <select
                    name="channelCode"
                    css={selectStyle}
                    onChange={e => updateChannelCode(e.target.value)}
                    value={channelCodeId ?? ''}
                  >
                    <option value="">-</option>
                    {channelCodeMasters.map(itemChannelCode => (
                      <option key={itemChannelCode.id} value={itemChannelCode.id}>
                        {itemChannelCode.name}
                      </option>
                    ))}
                    {reservation?.channelCodeId && !channelCodeMasters.some(({ id }) => reservation?.channelCodeId === id) && (
                      <option value={reservation?.channelCodeId} selected>
                        {reservation?.channelCodeName}
                      </option>
                    )}
                  </select>
                </div>
              </div>
              {/* segment */}
              <div css={reservationRoomContainerStyle}>
                <img src={require('@/static/images/segment-icon3.svg')} />
                <div>
                  <select
                    name="marketSegment"
                    css={selectStyle}
                    onChange={e => setMarketSegmentId(e.target.value)}
                    value={marketSegmentId ?? ''}
                  >
                    <option value="">-</option>
                    {segmentMasters.map(item => (
                      <option key={item.id} value={item.id}>
                        {item.name}
                      </option>
                    ))}
                    {reservation?.marketSegmentId && !segmentMasters.some(({ id }) => reservation?.marketSegmentId === id) && (
                      <option value={reservation?.marketSegmentId} selected>
                        {reservation?.marketSegmentName}
                      </option>
                    )}
                  </select>
                </div>
              </div>
              {/* plan name */}
              <div css={reservationRoomContainerStyle}>
                <img alt="icon_plan_name" src={require('@/static/images/plan.svg')} />
                <ul>{reservation?.plan || '-'}</ul>
              </div>
              {/* reserved room */}
              <div css={reservationRoomContainerStyle}>
                <img alt="icon_plan_name" src={require('@/static/images/icon_room.svg')} />
                <ul>{_reservationGuestRoom()}</ul>
              </div>

              {/* meal */}
              <div css={listMealStyle}>
                <img alt="eat" src={require('@/static/images/eat.svg')} />
                <p css={listTextStyle}>{_displayMeal(reservation?.meal)}</p>
              </div>

              {/* payment */}
              <div css={paymentPlanStyle}>
                <img alt="pay" src={require('@/static/images/pay.svg')} />
                {reservation?.totalAmount ? `¥${Number(reservation.totalAmount).toLocaleString()}` : '-'} /{' '}
                {reservation?.payment || t('Local payment')}
              </div>
              <div css={paymentPlanDetailStyle}>
                <span>
                  {t('Accommodation fee')} :{' '}
                  {reservation?.accommodationFee ? `¥${Number(reservation.accommodationFee).toLocaleString()}` : '-'}
                </span>
                <span>
                  {t('Point discount')} :{reservation?.pointDiscount ? ` -¥${Number(reservation.pointDiscount).toLocaleString()}` : ' -'}
                </span>
              </div>
            </div>
            {reservation.telegramData ? (
              <div style={{ minWidth: 0, padding: '0 16px 32px' }}>
                {isTelegramDataVisible && (
                  <pre
                    style={{
                      lineHeight: 1.5,
                      fontFamily: 'inherit',
                      fontSize: 14,
                      marginTop: 16,
                      whiteSpace: 'pre',
                    }}
                  >
                    {reservation.telegramData}
                  </pre>
                )}
                <button
                  type="button"
                  css={[telegramToggleStyle, isTelegramDataVisible && telegramToggleExpandedStyle]}
                  onClick={() => setIsTelegramDataVisible(!isTelegramDataVisible)}
                  aria-expanded={isTelegramDataVisible}
                  style={{ padding: '0 54px' }}
                >
                  {isTelegramDataVisible ? '閉じる' : '原文を見る'}
                  <img alt="icon_arrow_up" src={require('@/static/images/icon_more_view_arrow.svg')} />
                </button>
              </div>
            ) : null}
          </div>
        </div>
      </div>
      {reservationDetailOpen && (
        <div css={reservationInfoDetailStyle}>
          <div css={detailListWrapper}>
            <ReservationUserGuest
              userOrGuest={'user'}
              kana={reservation?.reservationUserNameKana || reservation?.userNameKana || '-'}
              name={reservation?.reservationUserName || reservation?.userName || '-'}
              tel={reservation?.reservationUserTel || reservation?.userTel || '-'}
              mail={reservation?.reservationUserMailAddr || reservation?.userMailAddr || '-'}
              address={reservation?.reservationUserAddr || reservation?.userAddr || '-'}
            />
            <div className="divider" />
            <ReservationUserGuest
              userOrGuest={'guest'}
              kana={reservation?.guestNameKana || '-'}
              name={reservation?.guestName || '-'}
              tel={reservation?.guestTel || '-'}
              mail={reservation?.guestMailAddr || '-'}
              address={reservation?.guestAddr || '-'}
            />
          </div>
        </div>
      )}
      {showEditFooter ? (
        <div css={bottomContentStyle} className="show-edit">
          <Button buttonType={3} width={100} height={30} fontSize={12} marginRight={16} onClick={resetReservationData}>
            {t('Cancel')}
          </Button>
          <Button buttonType={1} width={100} height={30} fontSize={12} onClick={onSaveData}>
            {t('Save')}
          </Button>
        </div>
      ) : (
        <div css={bottomContentStyle}>
          <div className="id-block">
            <p>{`${t('Reservation ID')} : ${reservation?.reservationId === undefined ? '-' : reservation.reservationId}`}</p>
          </div>
          <div className="detail-close-block" onClick={() => setReservationDetailOpen(!reservationDetailOpen)}>
            <p>{t(reservationDetailOpen ? 'Close up' : 'View reservation user details')}</p>
            <img className={reservationDetailOpen ? 'open' : ''} alt="icon_arrow_down" src={require(`@/static/images/arrow_down.svg`)} />
          </div>
        </div>
      )}
      <AssignConfirmDialog />
    </div>
  )
}

const reservationInfoStyle = css({
  borderRadius: '10px 10px 0 0',
  border: '1px solid #CCCCCC',
  borderBottom: 'none',
  overflow: 'hidden',
})

const topContentStyle = css({
  paddingLeft: 32,
  paddingRight: 32,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  borderBottom: '1px solid #CCCCCC',
  '@media(max-width: 1080px)': {
    paddingInline: 16,
  },
})

const bottomContentStyle = css({
  paddingLeft: 32,
  paddingRight: 30,
  display: 'flex',
  alignItems: 'center',
  border: '1px solid #CCCCCC',
  borderRadius: '0 0 10px 10px',
  borderTop: '0',
  height: 54,
  justifyContent: 'space-between',
  '&.show-edit': {
    justifyContent: 'end',
    padding: '12px 32px',
  },
  '@media(max-width: 1080px)': { paddingInline: 16 },
  '.id-block': {
    fontSize: 12,
    color: '#676767',
    fontWeight: 'normal',
    display: 'flex',
    overflow: 'hidden',
    p: { margin: '0 24px 0 0' },
  },
  '.detail-close-block': {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    color: '#F2A40B',
    fontSize: 12,
    padding: '8px 0',
    cursor: 'pointer',
    fontWeight: 'bold',
    borderBottom: 'none',
    marginLeft: 0,
    img: { '&.open': { transform: 'rotate(180deg)' } },
  },
})

const arrowStyle = css({
  position: 'absolute',
  right: 8,
  top: 9,
})

const dropDownContentStyle = css({
  display: 'flex',
  height: 34,
  listStyle: 'none',
  alignItems: 'center',
  padding: '0 16px',
  cursor: 'pointer',
  '&:hover': {
    background: '#f2f2f2',
  },
})

const selectStyle = css(initializeSelectBoxStyle, {
  display: 'block',
  height: 32,
  borderRadius: 16,
  textAlign: 'left',
  fontSize: 14,
  border: '1px solid #CCCCCC',
  backgroundImage: `url(${require('@/static/images/select.svg')})`,
  backgroundRepeat: 'no-repeat',
  backgroundSize: 10,
  paddingLeft: 20,
  paddingRight: 34,
  cursor: 'pointer',
  backgroundPosition: '92% center',
  width: '205px',
})

const dropDownTitleStyle = css({
  fontSize: 14,
  fontWeight: 'normal',
  color: '#676767',
})

const reservationStatusStyle = css({
  width: 116,
  height: 24,
  border: '1px solid #E7A73C',
  borderRadius: 5,
  color: '#E7A73C',
  fontSize: 12,
  fontWeight: 'bold',
  textAlign: 'center',
  padding: '6px 0',
  marginTop: 11,
  marginBottom: 10,
  cursor: 'pointer',
  position: 'relative',
})

// 予約詳細
const listWrapperStyle = css({
  backgroundColor: '#FFFFFF',
  width: '100%',
  minHeight: 386,
  borderBottom: 'solid 1px #CCCCCC',
  display: 'flex',
  alignItems: 'stretch',
  '> div:not(.divider)': {
    flex: 1,
    minWidth: 0,
    '@media(max-width: 1080px)': {
      paddingInline: 16,
    },
  },
  '> .divider': {
    width: 1,
    backgroundColor: '#ccc',
    margin: '24px 0',
  },
})

// 宿泊日
const checkInDateStyle = css({
  display: 'flex',
  alignItems: 'center',
  fontWeight: 'bold',
  '> img': {
    marginRight: 18,
  },
  '.SingleDatePickerInput': {
    display: 'flex',
    width: 'auto',
    '@media(max-width: 1080px)': {
      width: 126,
    },
    '.DateInput': {
      width: '100%',
    },
    '.SingleDatePicker_picker': {
      top: '12px !important',
    },
    '.SingleDatePickerInput_calendarIcon': {
      display: 'none',
    },
  },
})

// 宿泊人数
const numberOfCustomerStyle = css(checkInDateStyle, {
  marginTop: 25,
})

const numberOfCustomerDetailStyle = css({
  margin: '8px 0 0 37px',
  fontSize: 14,
  color: '#676767',
  letterSpacing: 0.7,
  alignItems: 'center',
  span: {
    margin: '0 18px 0 0',
  },
})

// 予約部屋
const reservationRoomContainerStyle = css(checkInDateStyle, {
  display: 'flex',
  marginTop: 24,
  '> img': { width: 20 },
})

const listTextStyle = css({})

// 食事
const listMealStyle = css(checkInDateStyle, {
  margin: '24px 0 0 0',
})

// 支払い
const paymentPlanStyle = css(checkInDateStyle, {
  margin: '28px 0 0 0',
})

const paymentPlanDetailStyle = css(numberOfCustomerDetailStyle, {
  margin: '8px 0 0 38px',
})

// メモ
const listMemoStyle = css({
  margin: '24px 25px 0 0',
  fontSize: 14,
  fontWeight: 'normal',
  color: '#676767',
  letterSpacing: 0.7,
  display: '-webkit-inline-box',
  lineHeight: '1.4',
  overflowWrap: 'anywhere',
  img: {
    margin: '0 11px 0 0',
  },
})

const reservationInfoDetailStyle = css({
  background: '#fff',
  color: '#676767',
  borderLeft: '1px solid #CCCCCC',
  borderRight: '1px solid #CCCCCC',
})

const detailListWrapper = css({
  background: '#fff',
  width: '100%',
  borderBottom: '1px solid #CCCCCC',
  display: 'flex',
  alignItems: 'stretch',
  '> .divider': {
    width: 1,
    backgroundColor: '#ccc',
    margin: '32px 0',
  },
})

const wavyLineStyle = css({
  padding: '0 7px',
})

const hintStyle = css({
  '.help-icon': { width: 18, height: 18 },
  '.help-popup': { left: 26, top: 0, width: 400 },
  '.hint-text': {
    color: '#676767',
    fontSize: 12,
    lineHeight: 1.75,
    padding: '12px 16px',
  },
})

const planEditButtonStyle = css({
  width: 32,
  height: 32,
  borderRadius: '50%',
  border: '1px solid #CCCCCC',
  cursor: 'pointer',
  backgroundColor: '#FFF',
})

const displayChildStyle = css({
  display: 'flex',
  alignItems: 'center',
  marginTop: 11,
})

const telegramToggleStyle = css({
  display: 'flex',
  alignItems: 'center',
  marginTop: 16,
  cursor: 'pointer',
  fontSize: 12,
  border: 0,
  background: 'none',
  padding: 0,
  color: '#676767',
  img: {
    width: 20,
    height: 20,
    transition: 'transform 300ms',
  },
})

const telegramToggleExpandedStyle = css({
  marginTop: 8,
  img: {
    transform: 'rotate(-180deg)',
  },
})
